import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Cloud, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem("admin_token", res.data.token);
      localStorage.setItem("admin_user", res.data.username);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-screen flex items-center justify-center animated-grid"
      style={{ background: "#030305" }}
    >
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 50%, rgba(0,243,255,0.05) 0%, transparent 70%)" }} />

      <div className="w-full max-w-md px-6 relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #00F3FF, #BC13FE)", boxShadow: "0 0 20px rgba(0,243,255,0.4)" }}>
              <Cloud size={20} color="#030305" strokeWidth={2.5} />
            </div>
          </div>
          <h1 className="font-orbitron font-700 text-xl text-gradient tracking-wider">INDIACLOUD.AI</h1>
          <p className="font-rajdhani font-600 text-sm text-gray-500 mt-1 tracking-widest">ADMIN PANEL</p>
        </div>

        {/* Form card */}
        <div className="glass rounded-2xl p-8"
          style={{ boxShadow: "0 0 40px rgba(0,243,255,0.08)" }}>
          <div className="flex items-center gap-2 mb-6">
            <Lock size={16} color="#00F3FF" />
            <h2 className="font-rajdhani font-700 text-base text-white tracking-wide">SECURE LOGIN</h2>
          </div>

          <form onSubmit={login} className="flex flex-col gap-5">
            <div>
              <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-2">USERNAME</label>
              <input
                data-testid="admin-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter admin username"
                className="neon-input"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-2">PASSWORD</label>
              <div className="relative">
                <input
                  data-testid="admin-password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="neon-input pr-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm"
                style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#fca5a5" }}>
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="btn-primary py-3 font-700 text-sm tracking-wider flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <div className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
              ) : (
                <>
                  <Lock size={14} /> LOGIN TO ADMIN PANEL
                </>
              )}
            </button>
          </form>

          <p className="font-outfit text-xs text-gray-700 text-center mt-5" style={{ fontWeight: 300 }}>
            Default: admin / IndiaCloud@2024
          </p>
        </div>
      </div>
    </div>
  );
}
